import { applyGermanize } from './germanize.js';
import { STORAGE_PREFIX, useFocusTrap, useIsMobile, usePersistentState } from './hooks.js';
import { IS_TOUCH, LK_ZONES, buildVerwechslungsEdges, getPatternProgress, loadGlobal } from './logic.js';
import { Header } from './navigation.jsx';
import { accent, accentBorder, accentDim, bg, border, borderLight, surface, surface2, textDim, textMuted } from './tokens.js';

const { useState, useEffect } = React;

// ─── ErkundungsPanel ─────────────────────────────────────────────────────
export function ErkundungsPanel({ onSelect }) {
  const [openGroup, setOpenGroup] = useState(null);
  const sortedGroups = Object.entries(GROUPS).sort((a, b) => a[1].order - b[1].order);
  const lastVisited = loadGlobal("lastVisited");
  const lastPattern = lastVisited && PATTERNS[lastVisited] ? PATTERNS[lastVisited] : null;

  return (
    <div style={{display: "flex", flexDirection: "column", gap: 10}}>
      {lastPattern && (
        <div style={{marginBottom: 6}}>
          <div style={{fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: textDim, marginBottom: 8}}>
            {applyGermanize("Zuletzt erkundet")}
          </div>
          <button onClick={() => onSelect(lastVisited)} style={{
            background: accentDim, border: `1px solid ${accentBorder}`, borderRadius: 20,
            padding: "5px 14px", color: accent, fontSize: 13, fontFamily: "inherit",
            cursor: "pointer", fontWeight: 600
          }}>
            {applyGermanize(lastPattern.title)}
          </button>
        </div>
      )}

      {sortedGroups.map(([key, g]) => {
        const started = g.members.filter(id => getPatternProgress(id) === "durchgearbeitet").length;
        const pct = g.members.length ? (started / g.members.length) * 100 : 0;
        const isOpen = openGroup === key;
        return (
          <div key={key} style={{background: surface, borderRadius: 10, border: `1px solid ${border}`, overflow: "hidden"}}>
            <button onClick={() => setOpenGroup(isOpen ? null : key)} aria-expanded={isOpen} style={{
              width: "100%", background: "none", border: "none", padding: "14px 16px",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left"
            }}>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{fontSize: 13, fontWeight: 600, color: "#e8e6e3", marginBottom: 8}}>
                  {applyGermanize(g.label)}
                </div>
                <div style={{height: 3, borderRadius: 2, background: border, overflow: "hidden"}}>
                  <div style={{height: "100%", width: pct + "%", background: pct === 100 ? accent : accentDim, borderRadius: 2, transition: "width 0.3s ease"}} />
                </div>
              </div>
              <span style={{fontSize: 12, color: textDim, flexShrink: 0, minWidth: 40, textAlign: "right"}}>
                {started} / {g.members.length}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease"}}>
                <path d="M2 4l4 4 4-4" stroke={textDim} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {isOpen && (
              <div style={{padding: "0 16px 14px", display: "flex", flexWrap: "wrap", gap: 6}}>
                {g.members.map(id => {
                  const pat = PATTERNS[id];
                  if (!pat) return null;
                  const prog = getPatternProgress(id);
                  const styles = prog === "durchgearbeitet"
                    ? {background: accent, color: bg, border: `1px solid ${accent}`}
                    : prog === "angefangen"
                    ? {background: accentDim, color: accent, border: `1px solid ${accentBorder}`}
                    : {background: "transparent", color: textDim, border: `1px solid ${borderLight}`};
                  return (
                    <button key={id} onClick={() => onSelect(id)}
                      title={prog === "durchgearbeitet" ? "Vertieft" : prog === "angefangen" ? "Angefangen" : "Noch nicht erkundet"}
                      style={{...styles, borderRadius: 20, padding: "4px 10px", fontSize: 12, fontFamily: "inherit", cursor: "pointer", fontWeight: prog === "durchgearbeitet" ? 600 : 400}}>
                      {applyGermanize(pat.title)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── LernpfadePanel ──────────────────────────────────────────────────────
export function LernpfadePanel({ onSelectWithMode }) {
  const curated = (typeof LERNPFADE !== "undefined" && Array.isArray(LERNPFADE)) ? LERNPFADE : [];
  const [customPfade, setCustomPfade] = usePersistentState("global.pfade", []);
  const [showCreator, setShowCreator] = useState(false);
  const [creatorTitle, setCreatorTitle] = useState("");
  const [creatorSelected, setCreatorSelected] = useState([]);
  const creatorTrapRef = useFocusTrap(showCreator);
  const sortedGroups = Object.entries(GROUPS).sort((a, b) => a[1].order - b[1].order);

  function cancelCreator() { setShowCreator(false); setCreatorTitle(""); setCreatorSelected([]); }
  function toggleSelect(id) {
    setCreatorSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function saveCustom() {
    if (!creatorTitle.trim() || creatorSelected.length < 1) return;
    const newPfad = { id: "custom_" + Date.now(), title: creatorTitle.trim(), stations: creatorSelected.map(id => ({ id, mode: "erkennen" })) };
    setCustomPfade([...customPfade, newPfad]);
    cancelCreator();
  }

  useEffect(() => {
    if (!showCreator) return;
    const h = (e) => { if (e.key === "Escape") cancelCreator(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [showCreator]);

  function renderPath(path, isCustom) {
    const started = path.stations.filter(s => getPatternProgress(s.id) === "durchgearbeitet").length;
    const total = path.stations.length;
    const pct = total ? (started / total) * 100 : 0;
    const nextStation = path.stations.find(s => getPatternProgress(s.id) !== "durchgearbeitet") || path.stations[0];
    const ctaLabel = started === 0 ? "Pfad starten" : started === total ? "Wiederholen" : "Weiter";
    return (
      <div key={path.id} style={{position: "relative", background: surface, borderRadius: 10, border: `1px solid ${border}`, padding: "16px 18px"}}>
        {isCustom && (
          <button onClick={() => setCustomPfade(customPfade.filter(p => p.id !== path.id))}
            aria-label={applyGermanize("Pfad loeschen")} title={applyGermanize("Pfad loeschen")}
            style={{position: "absolute", top: 10, right: 12, background: "none", border: "none", cursor: "pointer", color: textDim, fontSize: 14, padding: 4, lineHeight: 1}}>✕</button>
        )}
        {path.onboarding && (
          <div style={{display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: accent, border: `1px solid ${accentBorder}`, borderRadius: 4, padding: "2px 7px", marginBottom: 10}}>
            {applyGermanize("Einstieg")}
          </div>
        )}
        {isCustom && (
          <div style={{display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: textDim, border: `1px solid ${borderLight}`, borderRadius: 4, padding: "2px 7px", marginBottom: 10}}>
            {applyGermanize("Eigener Pfad")}
          </div>
        )}
        <div style={{fontSize: 15, fontWeight: 700, color: "#e8e6e3", marginBottom: 4, paddingRight: isCustom ? 22 : 0}}>{applyGermanize(path.title)}</div>
        {path.leitfrage && (
          <div style={{fontSize: 13, color: textMuted, marginBottom: 12, fontStyle: "italic", fontFamily: "Georgia, serif"}}>{applyGermanize(path.leitfrage)}</div>
        )}
        <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, marginTop: path.leitfrage ? 0 : 8, marginBottom: 14}}>
          {path.stations.map((s, i) => {
            const done = getPatternProgress(s.id) === "durchgearbeitet";
            const pat = PATTERNS[s.id];
            return (
              <React.Fragment key={s.id + i}>
                <button onClick={() => onSelectWithMode(s.id, s.mode)} title={applyGermanize("Zur Station")}
                  style={{fontSize: 11, padding: "2px 8px", borderRadius: 12, background: done ? accentDim : "transparent", color: done ? accent : textDim, border: `1px solid ${done ? accentBorder : borderLight}`, whiteSpace: "nowrap", fontFamily: "inherit", cursor: "pointer"}}>
                  {applyGermanize(pat ? pat.title : s.id)}
                </button>
                {i < path.stations.length - 1 && <span style={{color: textDim, fontSize: 11}}>→</span>}
              </React.Fragment>
            );
          })}
        </div>
        <div style={{display: "flex", alignItems: "center", gap: 14}}>
          <div style={{flex: 1}}>
            <div style={{height: 3, borderRadius: 2, background: border, overflow: "hidden"}}>
              <div style={{height: "100%", width: pct + "%", background: pct === 100 ? accent : accentDim, borderRadius: 2, transition: "width 0.3s ease"}} />
            </div>
            <div style={{fontSize: 11, color: textDim, marginTop: 5}}>{started} {applyGermanize("von")} {total} {applyGermanize("vertieft")}</div>
          </div>
          <button onClick={() => onSelectWithMode(nextStation.id, nextStation.mode)}
            style={{background: accent, border: "none", borderRadius: 7, padding: "7px 14px", color: bg, fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap"}}>
            {applyGermanize(ctaLabel)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{display: "flex", flexDirection: "column", gap: 14}}>
      {curated.map(p => renderPath(p, false))}
      {(Array.isArray(customPfade) ? customPfade : []).map(p => renderPath(p, true))}

      <button onClick={() => setShowCreator(true)}
        style={{background: "transparent", border: `1px dashed ${borderLight}`, borderRadius: 10, padding: "12px 18px", color: textMuted, fontFamily: "inherit", fontSize: 13, cursor: "pointer", textAlign: "center"}}>
        {applyGermanize("+ Eigenen Pfad erstellen")}
      </button>

      {showCreator && (
        <div ref={creatorTrapRef} role="dialog" aria-modal="true"
          style={{position: "fixed", inset: 0, background: "rgba(38,39,41,0.92)", zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 18px", overflowY: "auto"}}>
          <div
            style={{background: surface2, borderRadius: 12, border: `1px solid ${border}`, width: "100%", maxWidth: 540, padding: "22px 20px"}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18}}>
              <span style={{fontSize: 16, fontWeight: 700, color: "#e8e6e3"}}>{applyGermanize("Eigenen Pfad erstellen")}</span>
              <button onClick={cancelCreator} aria-label={applyGermanize("Schliessen")} style={{background: "none", border: "none", cursor: "pointer", color: textDim, fontSize: 16, padding: 4, lineHeight: 1}}>✕</button>
            </div>

            <div style={{fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: textDim, marginBottom: 6}}>{applyGermanize("Titel")}</div>
            <input autoFocus value={creatorTitle} onChange={e => setCreatorTitle(e.target.value)} placeholder={applyGermanize("Mein Pfad")} maxLength={60}
              style={{width: "100%", background: bg, border: `1px solid ${border}`, borderRadius: 7, padding: "8px 12px", color: "#e8e6e3", fontFamily: "inherit", fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 18}} />

            {creatorSelected.length > 0 && (
              <div style={{marginBottom: 14}}>
                <div style={{fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: textDim, marginBottom: 6}}>{applyGermanize("Ausgewaehlt")} ({creatorSelected.length})</div>
                <div style={{display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center"}}>
                  {creatorSelected.map((id, i) => (
                    <React.Fragment key={id + i}>
                      <button onClick={() => toggleSelect(id)}
                        style={{fontSize: 11, padding: "2px 8px", borderRadius: 12, background: accentDim, color: accent, border: `1px solid ${accentBorder}`, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap"}}>
                        {applyGermanize(PATTERNS[id] ? PATTERNS[id].title : id)} ✕
                      </button>
                      {i < creatorSelected.length - 1 && <span style={{color: textDim, fontSize: 11}}>→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            <div style={{fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: textDim, marginBottom: 10}}>{applyGermanize("Muster auswaehlen")}</div>
            <div style={{display: "flex", flexDirection: "column", gap: 12, marginBottom: 20}}>
              {sortedGroups.map(([key, g]) => (
                <div key={key}>
                  <div style={{fontSize: 11, color: textDim, marginBottom: 5}}>{applyGermanize(g.label)}</div>
                  <div style={{display: "flex", flexWrap: "wrap", gap: 4}}>
                    {g.members.map(id => {
                      const pat = PATTERNS[id];
                      const sel = creatorSelected.includes(id);
                      return (
                        <button key={id} onClick={() => toggleSelect(id)}
                          style={{fontSize: 11, padding: "2px 8px", borderRadius: 12, background: sel ? accentDim : "transparent", color: sel ? accent : textDim, border: `1px solid ${sel ? accentBorder : borderLight}`, fontFamily: "inherit", cursor: "pointer", fontWeight: sel ? 600 : 400, whiteSpace: "nowrap"}}>
                          {applyGermanize(pat ? pat.title : id)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{display: "flex", gap: 10, justifyContent: "flex-end"}}>
              <button onClick={cancelCreator}
                style={{background: "transparent", border: `1px solid ${border}`, borderRadius: 7, padding: "7px 16px", color: textMuted, fontSize: 13, fontFamily: "inherit", cursor: "pointer"}}>
                {applyGermanize("Abbrechen")}
              </button>
              <button onClick={saveCustom} disabled={!creatorTitle.trim() || creatorSelected.length < 1}
                style={{background: (!creatorTitle.trim() || creatorSelected.length < 1) ? border : accent, border: "none", borderRadius: 7, padding: "7px 16px", color: (!creatorTitle.trim() || creatorSelected.length < 1) ? textDim : bg, fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: (!creatorTitle.trim() || creatorSelected.length < 1) ? "not-allowed" : "pointer"}}>
                {applyGermanize("Pfad speichern")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LandkartePanel ──────────────────────────────────────────────────────
// Verwechslungs-Erklaerung fuer eine Kante aus den ✗-Snippets (mit {other} aufgeloest)
export function explainEdge(aId, bId) {
  function find(fromId, toId) {
    var p = PATTERNS[fromId]; if (!p || !p.dialogSnippets) return null;
    var fallback = (p.similarTo && p.similarTo[0]) || null;
    for (var i = 0; i < p.dialogSnippets.length; i++) {
      var sn = p.dialogSnippets[i];
      if (sn && sn.isThis === false && sn.explanation && (sn.otherPattern || fallback) === toId) {
        return applyGermanize(sn.explanation).replace(/\{other\}/g, PATTERNS[toId] ? PATTERNS[toId].title : toId);
      }
    }
    return null;
  }
  return find(aId, bId) || find(bId, aId);
}

export function LandkartePanel({ onSelect }) {
  const mobile = useIsMobile();
  const [hovered, setHovered] = useState(null);
  const [focused, setFocused] = useState(null);
  const W = 880, H = 690, angeFill = "rgba(245,118,105,0.30)";

  const map = React.useMemo(function() {
    var pos = {}, grp = {};
    Object.entries(GROUPS).sort(function(a, b) { return a[1].order - b[1].order; }).forEach(function(entry) {
      var k = entry[0], g = entry[1], z = LK_ZONES[k];
      g.members.forEach(function(id, i) { grp[id] = k; var ang = -Math.PI/2 + 2*Math.PI*i/g.members.length; pos[id] = { x: z.cx + z.r*Math.cos(ang), y: z.cy + z.r*Math.sin(ang) }; });
    });
    var edges = buildVerwechslungsEdges();
    var adj = {}; edges.forEach(function(e) { (adj[e[0]] = adj[e[0]] || []).push(e[1]); (adj[e[1]] = adj[e[1]] || []).push(e[0]); });
    return { pos: pos, grp: grp, edges: edges, adj: adj };
  }, []);

  const pos = map.pos, grp = map.grp, edges = map.edges, adj = map.adj;
  const active = focused || hovered;
  const neigh = active ? (adj[active] || []) : [];
  const neighSet = active ? new Set(neigh) : null;
  const panelNeigh = focused ? (adj[focused] || []) : [];

  function fillFor(id) { var pr = getPatternProgress(id); return pr === "durchgearbeitet" ? accent : (pr === "angefangen" ? angeFill : bg); }
  function strokeFor(id) { return getPatternProgress(id) === "unberuehrt" ? borderLight : accent; }
  function leg(swatch, text) { return <span style={{display:"inline-flex", alignItems:"center", gap:6}}>{swatch}{text}</span>; }

  return (
    <div>
      <div style={{display:"flex", gap:14, flexWrap:"wrap", alignItems:"center", marginBottom:12, fontSize:12, color:textMuted}}>
        {leg(<svg width="14" height="14"><circle cx="7" cy="7" r="5" fill={accent}/></svg>, applyGermanize("vertieft"))}
        {leg(<svg width="14" height="14"><circle cx="7" cy="7" r="5" fill={angeFill} stroke={accent} strokeWidth="1.3"/></svg>, applyGermanize("angefangen"))}
        {leg(<svg width="14" height="14"><circle cx="7" cy="7" r="5" fill={bg} stroke={borderLight} strokeWidth="1.3"/></svg>, applyGermanize("unberuehrt"))}
        {leg(<svg width="24" height="8"><line x1="1" y1="4" x2="23" y2="4" stroke={borderLight} strokeWidth="1.5"/></svg>, applyGermanize("verwandt"))}
        {leg(<svg width="24" height="8"><line x1="1" y1="4" x2="23" y2="4" stroke={accent} strokeWidth="2"/></svg>, applyGermanize("Bruecke zwischen Gruppen"))}
      </div>

      <div style={{display:"flex", flexDirection: mobile ? "column" : "row", gap:16, alignItems:"flex-start"}}>
        {/* Graph */}
        <div style={{width: mobile ? "100%" : "auto", flex: mobile ? "1 1 auto" : "1 1 0", minWidth:0, overflowX:"auto"}}>
          <svg viewBox={"0 0 " + W + " " + H} width={mobile ? 760 : "100%"} style={{height:"auto", display:"block"}}
               onClick={() => { setFocused(null); setHovered(null); }}>
            {Object.keys(LK_ZONES).map(function(k) { var z = LK_ZONES[k]; return (
              <g key={k}>
                <circle cx={z.cx} cy={z.cy} r={z.r + 30} fill="rgba(245,118,105,0.045)" stroke={border} />
                <text x={z.cx} y={z.cy - z.r - 40} textAnchor="middle" fill={textMuted} fontSize="15" fontWeight="600">{applyGermanize(z.label)}</text>
              </g>
            ); })}
            {edges.map(function(e, i) { var a = e[0], b = e[1], pa = pos[a], pb = pos[b]; if (!pa || !pb) return null; var cross = grp[a] !== grp[b]; var op = active ? ((a === active || b === active) ? 0.95 : 0.03) : (cross ? 0.22 : 0.12); return (
              <line key={i} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={cross ? accent : borderLight} strokeWidth={cross ? 2 : 1.4} strokeOpacity={op} style={{transition:"stroke-opacity .12s"}} />
            ); })}
            {Object.keys(pos).map(function(id) { var p = pos[id]; var isA = active === id; var isN = active && (id === active || neighSet.has(id)); var op = active ? (isN ? 1 : 0.16) : 1; return (
              <circle key={id} cx={p.x} cy={p.y} r={isA ? 9.5 : 6.5} fill={isA ? accent : fillFor(id)} stroke={isA ? accent : strokeFor(id)} strokeWidth="1.5"
                style={{cursor:"pointer", opacity:op, transition:"opacity .12s"}}
                onMouseEnter={function() { if (!IS_TOUCH) setHovered(id); }}
                onMouseLeave={function() { if (!IS_TOUCH) setHovered(null); }}
                onClick={function(e) { e.stopPropagation(); setFocused(id); }}>
                <title>{applyGermanize(PATTERNS[id].title)}</title>
              </circle>
            ); })}
            {active && [active].concat(neigh).map(function(id, i) { var p = pos[id]; if (!p) return null; var left = p.x < W/2; var isH = id === active; var title = applyGermanize(PATTERNS[id].title); var w = title.length * 7 + 12; var tx = left ? p.x + 12 : p.x - 12; var rectX = left ? tx - 5 : tx - w - 5; return (
              <g key={"lab" + i} style={{pointerEvents:"none"}}>
                <rect x={rectX} y={p.y - 10} width={w + 10} height={20} rx={5} fill="#1d1e20" fillOpacity={0.92} stroke={border} />
                <text x={tx} y={p.y + 4} textAnchor={left ? "start" : "end"} fontSize="12.5" fontWeight={isH ? 700 : 600} fill={isH ? accent : "#e8e6e3"}>{title}</text>
              </g>
            ); })}
          </svg>
        </div>

        {/* Info-Panel */}
        <div style={{width: mobile ? "100%" : "auto", flex: mobile ? "1 1 auto" : "0 0 300px"}}>
          {!focused ? (
            <p style={{fontSize:13, color:textDim, lineHeight:1.7, fontFamily:"Georgia, serif", fontStyle:"italic", margin:0}}>
              {applyGermanize("Klick auf ein Muster, um seine Verbindungen zu sehen.")}
            </p>
          ) : (
            <div>
              <div style={{fontSize:11, letterSpacing:1, textTransform:"uppercase", color:accent, marginBottom:4}}>{applyGermanize(PATTERNS[focused].subtitle)}</div>
              <h3 style={{fontFamily:"Georgia, serif", fontSize:18, fontWeight:400, color:"#e8e6e3", margin:0, lineHeight:1.2}}>
                {applyGermanize(PATTERNS[focused].title)}{PATTERNS[focused].titleEm ? <em style={{color:"#f5a99e", marginLeft:6, fontSize:"0.8em"}}>{applyGermanize(PATTERNS[focused].titleEm)}</em> : null}
              </h3>
              <div style={{fontSize:11, color:textDim, marginTop:6, marginBottom:12}}>{panelNeigh.length} {panelNeigh.length === 1 ? applyGermanize("Verbindung") : applyGermanize("Verbindungen")}</div>
              <button onClick={() => onSelect(focused)} style={{background:accent, border:"none", borderRadius:7, padding:"7px 14px", color:bg, fontSize:13, fontWeight:600, fontFamily:"inherit", cursor:"pointer", marginBottom:16}}>
                {applyGermanize("Zum Muster")}
              </button>
              <div style={{display:"flex", flexDirection:"column", gap:14}}>
                {panelNeigh.map(function(qid) { var ex = explainEdge(focused, qid); var cross = grp[focused] !== grp[qid]; return (
                  <div key={qid} style={{borderLeft:"2px solid " + (cross ? accentBorder : border), paddingLeft:10}}>
                    <button onClick={() => setFocused(qid)} style={{background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:600, color:"#e8e6e3", textAlign:"left"}}>
                      ↔ {applyGermanize(PATTERNS[qid] ? PATTERNS[qid].title : qid)}
                    </button>
                    {ex && <p style={{fontSize:13, color:textMuted, lineHeight:1.6, margin:"5px 0 0", fontFamily:"Georgia, serif"}}>{ex}</p>}
                  </div>
                ); })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MeinBereichView ─────────────────────────────────────────────────────
export function MeinBereichView({ onLogo, onAbout, onMein, tab, onTab, onSelect, userName, onNameChange, onSelectWithMode }) {
  const mobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(userName || "");

  const tabs = [
    { id: "erkundung", label: "01 Erkundungsreise" },
    { id: "lernpfade", label: "02 Lernpfade" },
    { id: "landkarte", label: "03 Landkarte" }
  ];

  function commitName() { onNameChange(draft.trim()); setEditing(false); }

  function exportBackup() {
    try {
      const data = {};
      Object.keys(localStorage)
        .filter(k => k.startsWith(STORAGE_PREFIX))
        .forEach(k => { data[k] = localStorage.getItem(k); });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "obscura-backup.json"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {}
  }

  function importBackup(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        if (typeof data !== "object" || data === null || Array.isArray(data)) throw new Error("invalid");
        const valid = Object.entries(data).filter(([k, v]) => k.startsWith(STORAGE_PREFIX) && typeof v === "string");
        if (valid.length === 0) throw new Error("leer");
        if (valid.length > 1000) throw new Error("zu viele");
        valid.forEach(([k, v]) => localStorage.setItem(k, v));
        window.location.reload();
      } catch (err) {
        alert("Import fehlgeschlagen – ungültige oder leere Datei.");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  return (
    <div style={{background: bg, minHeight: "100vh"}}>
      <Header onLogo={onLogo} onAbout={onAbout} onMein={onMein} userName={userName} />

      {/* Name / Begruessung — ueber der Auswahl */}
      <div style={{maxWidth: 720, margin: "0 auto", padding: mobile ? "24px 20px 18px" : "30px 24px 22px"}}>
        {editing ? (
          <div style={{display: "flex", gap: 8, alignItems: "center"}}>
            <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setDraft(userName || ""); setEditing(false); } }}
              placeholder="Dein Name" maxLength={40}
              style={{background: surface, border: `1px solid ${borderLight}`, borderRadius: 8, padding: "8px 12px", color: "#e8e6e3", fontFamily: "inherit", fontSize: 16, flex: 1, maxWidth: 260}} />
            <button onClick={commitName} style={{background: accentDim, border: `1px solid ${accentBorder}`, borderRadius: 8, padding: "8px 14px", color: accent, fontFamily: "inherit", fontSize: 13, cursor: "pointer"}}>
              {applyGermanize("Speichern")}
            </button>
          </div>
        ) : (
          <div>
            <button onClick={() => { setDraft(userName || ""); setEditing(true); }}
              aria-label={userName ? "Name aendern" : "Name hinzufuegen"}
              style={{background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 9, textAlign: "left"}}>
              <span style={{fontSize: mobile ? 26 : 32, fontWeight: 400, color: userName ? "#e8e6e3" : textDim, fontStyle: userName ? "normal" : "italic"}}>
                {userName || applyGermanize("Dein Name")}
              </span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0}}>
                <path d="M11.4 2.6l2 2L6 12l-2.6.6L4 10l7.4-7.4z" stroke={textDim} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div style={{fontSize: 13, color: textDim, marginTop: 6}}>
              {applyGermanize("Dein Raum – dein Fortschritt auf einen Blick.")}
            </div>
          </div>
        )}
        <div style={{display: "flex", gap: 10, marginTop: 16}}>
          <button onClick={exportBackup}
            style={{
              background: "none", border: `1px solid ${border}`, borderRadius: 100,
              cursor: "pointer", fontSize: 11, color: textMuted, padding: "4px 12px", fontFamily: "inherit"
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = borderLight; e.currentTarget.style.color = "#e8e6e3"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}>
            {applyGermanize("Export")}
          </button>
          <label
            style={{
              background: "none", border: `1px solid ${border}`, borderRadius: 100,
              cursor: "pointer", fontSize: 11, color: textMuted, padding: "4px 12px", fontFamily: "inherit", display: "inline-block"
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = borderLight; e.currentTarget.style.color = "#e8e6e3"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}>
            {applyGermanize("Import")}
            <input type="file" accept=".json" style={{display: "none"}} onChange={importBackup} />
          </label>
        </div>
      </div>

      {/* Tab-Leiste */}
      <div style={{display: "flex", justifyContent: "center", borderBottom: `1px solid ${border}`, padding: "0 18px"}}>
        {tabs.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id}
              onClick={() => { if (!t.soon) onTab(t.id); }}
              disabled={t.soon}
              title={t.soon ? "Bald" : undefined}
              style={{
                background: "none", border: "none", padding: "10px 13px",
                color: active ? accent : (t.soon ? borderLight : textDim),
                borderBottom: `2px solid ${active ? accent : "transparent"}`,
                cursor: t.soon ? "default" : "pointer", fontFamily: "inherit",
                fontSize: 12, letterSpacing: "0.03em"
              }}>
              {t.label}{t.soon && <span style={{fontSize: 9, marginLeft: 4, color: borderLight}}>bald</span>}
            </button>
          );
        })}
      </div>

      <main style={{maxWidth: tab === "landkarte" ? 1220 : 720, margin: "0 auto", padding: mobile ? "28px 20px 64px" : "32px 24px 80px"}}>
        {tab === "erkundung" ? (
          <ErkundungsPanel onSelect={onSelect} />
        ) : tab === "lernpfade" ? (
          <LernpfadePanel onSelectWithMode={onSelectWithMode} />
        ) : (
          <LandkartePanel onSelect={onSelect} />
        )}
      </main>
    </div>
  );
}
